import { DietaryRequirements } from "@/clients/models/DietaryRequirements";
import { Medical } from "@/clients/models/Medical";
import {
  MILK_PREFERENCE_MAP,
  MILK_PREFERENCE_REVERSE_MAP,
} from "@/constants/milk-preferences";
import { QueryKeys } from "@/constants/query-keys";
import { useGetDietaryRequirement } from "@/hooks/requests/dietary-requirement/use-get-dietary-requirement";
import { useUpdateDietaryRequirement } from "@/hooks/requests/dietary-requirement/use-update-dietary-requirement";
import { useGetMedical } from "@/hooks/requests/medical/use-get-medical";
import { useUpdateMedical } from "@/hooks/requests/medical/use-update-medical";
import { useToast } from "@/hooks/ui/use-toast";
import { setProfileContext } from "@/utils/sentry/tools";
import { IonSkeletonText } from "@ionic/react";
import { useQueryClient } from "@tanstack/react-query";
import { memo, useEffect, useState } from "react";
import { AllergensGroup } from "../../components/allergens-group";
import { LifestylePreferenceGroup } from "../../components/lifestyle-preference-group";
import { MilkPreferenceGroup } from "../../components/milk-preference-group";
import { SettingModalHeader } from "../../components/setting-modal-header";

interface DietaryRequirementPageProps {
  onBack?: () => void;
  isModal?: boolean;
}

const DietaryRequirementPage = memo(function DietaryRequirementPage({
  onBack,
  isModal,
}: DietaryRequirementPageProps) {
  const queryClient = useQueryClient();
  const { medical } = useGetMedical();
  const { dietaryRequirement, isLoading: isDietaryLoading } =
    useGetDietaryRequirement();
  const { updateDietaryRequirementAsync, isUpdating: isUpdatingDietary } =
    useUpdateDietaryRequirement();
  const { updateMedicalAsync, isUpdating: isUpdatingMedical } =
    useUpdateMedical();
  const { showError } = useToast();

  const [selectedDietaryOptions, setSelectedDietaryOptions] = useState<
    Set<string>
  >(new Set());
  const [selectedMilkOption, setSelectedMilkOption] = useState<string | null>(
    null,
  );
  const [selectedAllergyOptions, setSelectedAllergyOptions] = useState<
    Set<string>
  >(new Set());

  const isUpdating = isUpdatingDietary || isUpdatingMedical;

  useEffect(() => {
    if (dietaryRequirement) {
      setSelectedDietaryOptions(
        new Set(dietaryRequirement.dietaryOptions ?? []),
      );

      if (dietaryRequirement.milkPreference) {
        const displayValue =
          MILK_PREFERENCE_REVERSE_MAP[dietaryRequirement.milkPreference];
        setSelectedMilkOption(
          displayValue || dietaryRequirement.milkPreference,
        );
      }
    }

    if (medical) {
      setSelectedAllergyOptions(new Set(medical.allergies ?? []));
    }
  }, [dietaryRequirement, medical]);

  const handleSubmit = async () => {
    const milkPreferenceValue = selectedMilkOption
      ? MILK_PREFERENCE_MAP[selectedMilkOption] || selectedMilkOption
      : null;

    const dietaryDataBody: DietaryRequirements = {
      dietaryOptions: Array.from(selectedDietaryOptions),
      milkPreference: milkPreferenceValue,
    };

    const medicalDataBody: Medical = {
      medicalConditions: medical?.medicalConditions ?? [],
      allergies: Array.from(selectedAllergyOptions),
    };

    try {
      await Promise.all([
        updateDietaryRequirementAsync(dietaryDataBody),
        updateMedicalAsync(medicalDataBody),
      ]);

      queryClient.invalidateQueries({
        queryKey: [QueryKeys.DIETARY_REQUIREMENT],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.MEDICAL],
      });

      setProfileContext(QueryKeys.DIETARY_REQUIREMENT, dietaryDataBody);
      setProfileContext(QueryKeys.MEDICAL, medicalDataBody);

      onBack?.();
    } catch (error) {
      if (error instanceof Error) {
        showError(error.message);
      } else {
        showError("An unknown error occurred: " + error);
      }
    }
  };

  const toggleDietaryOption = (option: string) => {
    setSelectedDietaryOptions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(option)) {
        newSet.delete(option);
      } else {
        newSet.add(option);
      }
      return newSet;
    });
  };

  const toggleMilkOption = (option: string) => {
    setSelectedMilkOption((prev) => (prev === option ? null : option));
  };

  const toggleAllergyOption = (option: string) => {
    setSelectedAllergyOptions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(option)) {
        newSet.delete(option);
      } else {
        newSet.add(option);
      }
      return newSet;
    });
  };

  if (isDietaryLoading) {
    return (
      <div className="w-full h-full bg-[#fcfbfb] overflow-y-auto no-scrollbar">
        <SettingModalHeader title="Dietary Requirements" onBack={onBack} isModal={isModal} />
        <div className="px-9 pb-20 pt-8 space-y-8">
          {[1, 2, 3].map((section) => (
            <div key={section} className="flex flex-col gap-4">
              <IonSkeletonText animated className="h-6 w-24 rounded" />
              <div className="flex flex-wrap gap-3">
                {Array.from({ length: 4 }).map((_, i) => (
                  <IonSkeletonText
                    key={i}
                    animated
                    className="h-10 w-24 rounded-full"
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-[#fcfbfb] overflow-y-auto no-scrollbar">
      <SettingModalHeader
        title="Dietary Requirements"
        showSaveButton
        onSave={handleSubmit}
        onBack={onBack}
        isSaving={isUpdating}
        isModal={isModal}
      />

      <div className="px-9 pb-20 pt-4 space-y-8">
        <LifestylePreferenceGroup
          selectedOptions={selectedDietaryOptions}
          onToggleOption={toggleDietaryOption}
          disabled={isUpdating}
        />

        <MilkPreferenceGroup
          selectedOptions={
            selectedMilkOption ? new Set([selectedMilkOption]) : new Set()
          }
          onToggleOption={toggleMilkOption}
          disabled={isUpdating}
        />

        <AllergensGroup
          selectedOptions={selectedAllergyOptions}
          onToggleOption={toggleAllergyOption}
          disabled={isUpdating}
        />
      </div>
    </div>
  );
});

export default DietaryRequirementPage;
